import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader, Section, Field } from "../components/common";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { fdate, fdt } from "../lib/format";

function LaborRow({ c, canEdit, onSaved }) {
  const { t } = useT();
  const [f, setF] = useState({ hours_per_day: c.hours_per_day, overtime_factor: c.overtime_factor, min_margin_pct: c.min_margin_pct });
  const save = async () => {
    try { await api.put(`/countries/${c.code}`, f); toast.success(`${c.name}: ${t("common.saved")}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k) => <input type="number" step="any" disabled={!canEdit} data-testid={`labor-${c.code}-${k}`} className="cell-input num w-20" value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} />;
  return (
    <tr>
      <td className="font-bold">{c.code}</td><td>{c.name}</td><td className="text-neutral-500">{c.region}</td>
      <td>{inp("hours_per_day")}</td><td>{inp("overtime_factor")}</td><td>{inp("min_margin_pct")}</td>
      <td>{canEdit && <Button size="sm" variant="ghost" data-testid={`labor-save-${c.code}`} onClick={save}>{t("common.save")}</Button>}</td>
    </tr>
  );
}

export default function Settings() {
  const { t } = useT();
  const { can } = useAuth();
  const { data: rates, reload } = useFetch("/exchange-rates");
  const { data: countries, reload: reloadC } = useFetch("/countries");
  const [f, setF] = useState({ rate: "", source: "", effective_date: new Date().toISOString().slice(0, 10) });
  const canEdit = can("settings:write");
  const canFx = can("fx:write");
  const add = async () => {
    try { await api.post("/exchange-rates", { ...f, rate: Number(f.rate) }); toast.success(t("common.saved")); setF({ ...f, rate: "", source: "" }); reload(); window.dispatchEvent(new Event("fx:changed")); } catch (e) { toast.error(errMsg(e)); }
  };
  const cur = rates?.[0];
  return (
    <div className="space-y-6">
      <PageHeader title={t("set.title")} kicker={t("nav.group.admin")} />
      <div className="grid gap-6 xl:grid-cols-[1fr_1.4fr] [&>*]:min-w-0">
        <Section title={t("set.rates")} testid="section-rates">
          <div className="mb-4 rounded border border-neutral-200 bg-neutral-50 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{t("set.current")}</div>
            <div className="font-mono text-3xl font-bold text-[#db0720]" data-testid="current-rate">{cur ? Number(cur.rate).toFixed(4) : "—"}</div>
            <div className="text-xs text-neutral-500">{cur?.source} · {fdate(cur?.effective_date)}</div>
          </div>
          {canFx ? (
            <div className="mb-4 grid gap-3 sm:grid-cols-3">
              <Field label="USD/MXN" required><Input data-testid="new-rate-input" type="number" step="0.0001" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} /></Field>
              <Field label={t("set.effective")}><Input data-testid="new-rate-date" type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></Field>
              <Field label={t("set.source")}><Input data-testid="new-rate-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Field>
              <Button data-testid="add-rate-btn" onClick={add} disabled={!f.rate} className="sm:col-span-3">{t("set.addRate")}</Button>
            </div>
          ) : <p className="mb-3 text-xs font-semibold text-amber-800" data-testid="fx-admin-only">{t("ws.fxAdminOnly")}</p>}
          <p className="mb-3 text-[11px] text-neutral-500">{t("set.rateNote")}</p>
          <div className="overflow-x-auto"><table className="dense-table w-full" data-testid="rates-table">
            <thead><tr><th>{t("set.effective")}</th><th className="text-right">USD/MXN</th><th>{t("set.source")}</th><th>{t("common.user")}</th><th>{t("common.date")}</th></tr></thead>
            <tbody>{(rates || []).map((r) => <tr key={r.id}><td>{fdate(r.effective_date)}</td><td className="num font-semibold">{Number(r.rate).toFixed(4)}</td><td>{r.source}</td><td>{r.created_by}</td><td className="text-neutral-500">{fdt(r.created_at)}</td></tr>)}</tbody>
          </table></div>
        </Section>
        <Section title={t("set.labor")} testid="section-labor">
          <div className="overflow-x-auto">
            <table className="dense-table w-full">
              <thead><tr><th>ISO</th><th>{t("common.country")}</th><th>{t("set.region")}</th><th>{t("ws.hpd")}</th><th>{t("ws.otf")}</th><th>{t("ws.minMargin")} %</th><th /></tr></thead>
              <tbody>{(countries || []).map((c) => <LaborRow key={c.code} c={c} canEdit={canEdit} onSaved={reloadC} />)}</tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}
